import React, { useState } from 'react';
import {
    Alert,
    Button,
    ButtonGroup,
    CircularProgress,
    Grid2,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from '@mui/material';
import {
    useGetTimeslots,
    getTimeslots,
    deleteTimeslot,
    editTimeslot,
} from '@openapi/timeslots/timeslots';
import { useGetShow, syncShowICal } from '@openapi/shows/shows';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import useNotify from '../../hooks/useNotify';
import PageWithTitle from '../../components/atom/PageWithTitle';
import TimeslotCalendar from '../../components/TimeslotCalendar';
import AddIcon from '@mui/icons-material/Add';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import SyncIcon from '@mui/icons-material/Sync';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { collectAllData, exportToXlsx } from '../../helpers/excelExportHelpers';
import { getSlotTemplate } from '../../helpers/slotTemplate';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import { useCapabilities } from '../../hooks/useCapabilities';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';

const TimeslotsPage = () => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess, notifyInfo } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const { showModal } = useModals();
    const { showId } = useParams();
    const navigate = useNavigate();
    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [syncResults, setSyncResults] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [deletingTimeslotId, setDeletingTimeslotId] = useState(null);
    const {
        data,
        isLoading: loading,
        refetch: reloadData,
    } = useGetTimeslots(
        {
            optionalId: showId,
            pageSize: 10000,
            sort: 'starttime',
            sortDir: 'ASC',
            includeBlocks: true,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const { data: show, isLoading: loadingShow } = useGetShow(Number(showId), {
        query: {
            onError: notifyError,
        },
    });

    // Fetch ALL ticket types (not only show_in_list) so the inline capacity
    // editor can resolve every tier on a timeslot and send a complete reserved-
    // capacity map. Omitting hidden tiers would let EditTimeslot's full replace
    // wipe their reserved holds (an availability/oversell bug).
    const { data: ticketTypesData } = useGetTicketTypes(
        { optionalId: showId, onlyShowInList: false, includeSeats: true },
        { query: { onError: notifyError } },
    );
    const ticketTypes = ticketTypesData?.ticketTypes || [];

    const handleSyncICal = async () => {
        setSyncing(true);
        try {
            const result = await syncShowICal(showId);
            setSyncResults(result);
            setShowSyncModal(true);
            notifySuccess(t('timeslots.notifications.syncSuccess'));
            reloadData();
        } catch (error) {
            notifyError(error);
        } finally {
            setSyncing(false);
        }
    };

    const isICalShow = show?.timeslotMgmt === 'ical';
    const canManageTimeslots = can(CAPABILITIES.SHOWS_MANAGE);
    const canAssignSeats = can(CAPABILITIES.SEATING_ASSIGNMENTS);
    const delayedSeatAssignmentEnabled = !!show?.marketing?.delayedSeatAssignmentEnabled;
    const rentalMode = !!show?.marketing?.rentalMode;

    const handleInlineEditSave = async (timeslotId, editData) => {
        const timeslot = data?.timeslots?.find((ts) => ts.id === timeslotId);
        if (!timeslot) return;
        try {
            await editTimeslot(timeslotId, {
                showId: parseInt(timeslot.showId, 10),
                startTime: timeslot.startTime,
                ...editData,
            });
            notifySuccess(t('timeslots.notifications.updated'));
            reloadData();
        } catch (error) {
            notifyError(error);
            // Re-throw so the calendar keeps the inline editor open with the
            // unsaved values instead of silently closing on a failed save.
            throw error;
        }
    };

    const handleDeleteTimeslot = (timeslotId) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            content: t('confirmationModal.content'),
            confirm: async () => {
                setDeletingTimeslotId(timeslotId);
                let response;
                try {
                    response = await deleteTimeslot(timeslotId);
                } catch (_error) {
                    notifyError(t('timeslots.notifications.deleteError'));
                    setDeletingTimeslotId(null);
                    return;
                }
                notifySuccess(t('timeslots.notifications.deleted'));
                if (response?.message) {
                    notifyInfo(response.message);
                }
                reloadData();
                setDeletingTimeslotId(null);
            },
        });
    };

    return (
        <PageWithTitle
            title={`${t('timeslots.title')} (${loadingShow ? '' : show.title || ''})`}
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    key="search-and-buttons"
                >
                    <Grid2>
                        <ButtonGroup variant="contained">
                            {isICalShow && canManageTimeslots && (
                                <Tooltip title={t('timeslots.syncButton')}>
                                    <Button fullWidth disabled={syncing} onClick={handleSyncICal}>
                                        {syncing ? (
                                            <CircularProgress color="inherit" size={25} />
                                        ) : (
                                            <SyncIcon />
                                        )}
                                    </Button>
                                </Tooltip>
                            )}
                            {canManageTimeslots && (
                                <Tooltip title={t('timeslots.newTimeslotName')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/shows/${showId}/timeslots/new`);
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            )}
                            {canManageTimeslots && (
                                <Tooltip title={t('timeslots.bulkAddName')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/shows/${showId}/timeslots/bulk-add`);
                                        }}
                                    >
                                        <LibraryAddIcon />
                                    </Button>
                                </Tooltip>
                            )}
                            {canManageTimeslots && rentalMode && (
                                <Tooltip title={t('timeslots.createRentalDay')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/shows/${showId}/timeslots/rental-day`);
                                        }}
                                    >
                                        <BeachAccessIcon />
                                    </Button>
                                </Tooltip>
                            )}
                            {canManageTimeslots && rentalMode && (
                                <Tooltip title={t('timeslots.bulkRentalDaysName')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/shows/${showId}/timeslots/bulk-rental-day`);
                                        }}
                                    >
                                        <DateRangeIcon />
                                    </Button>
                                </Tooltip>
                            )}
                            {canManageTimeslots && (
                                <Tooltip title="Export">
                                    <Button
                                        fullWidth
                                        disabled={generatingXlsx}
                                        onClick={async () => {
                                            setGeneratingXlsx(true);
                                            let listToExport = [];
                                            try {
                                                listToExport = await collectAllData(
                                                    data,
                                                    getTimeslots,
                                                    setProgress,
                                                    'timeslots',
                                                    {
                                                        optionalId: showId,
                                                    },
                                                    'timeslots',
                                                );
                                                exportToXlsx(listToExport, 'timeslots');
                                                notifySuccess(
                                                    t('timeslots.notifications.exportSuccess'),
                                                );
                                            } catch (err) {
                                                notifyError(err);
                                            }
                                            setGeneratingXlsx(false);
                                            setProgress(0);
                                        }}
                                        label={t('common.export')}
                                    >
                                        {generatingXlsx ? (
                                            <CircularProgress
                                                variant="determinate"
                                                color="inherit"
                                                size={25}
                                                value={progress}
                                            />
                                        ) : (
                                            <FileDownloadIcon />
                                        )}
                                    </Button>
                                </Tooltip>
                            )}
                        </ButtonGroup>
                    </Grid2>
                </Grid2>,
            ]}
        >
            {isICalShow && (
                <Alert sx={{ width: '100%', mb: 2 }} variant="filled" severity="info">
                    {t('timeslots.alert')}
                </Alert>
            )}
            <Dialog
                open={showSyncModal}
                onClose={() => setShowSyncModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{t('timeslots.syncResults.title')}</DialogTitle>
                <DialogContent>
                    {syncResults && (
                        <>
                            <Typography variant="h5" gutterBottom>
                                {t('timeslots.syncResults.created')}
                            </Typography>
                            {syncResults.createdTimeslots?.length > 0 ? (
                                <ul>
                                    {syncResults.createdTimeslots.map((ts, idx) => (
                                        <li key={idx}>{formatDateTimeInVenueTz(ts.startTime)}</li>
                                    ))}
                                </ul>
                            ) : (
                                <Typography color="textSecondary" paragraph>
                                    {t('timeslots.syncResults.noCreated')}
                                </Typography>
                            )}

                            <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                                {t('timeslots.syncResults.removed')}
                            </Typography>
                            {syncResults.removedTimeslots?.length > 0 ? (
                                <>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        {t('timeslots.syncResults.warning')}
                                    </Alert>
                                    <ul>
                                        {syncResults.removedTimeslots.map((ts, idx) => (
                                            <li key={idx}>
                                                {formatDateTimeInVenueTz(ts.startTime)}
                                                <small>
                                                    {ts.bookingCount > 0
                                                        ? t('timeslots.syncResults.bookingCount', {
                                                              count: ts.bookingCount,
                                                          })
                                                        : null}
                                                </small>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <Typography color="textSecondary" paragraph>
                                    {t('timeslots.syncResults.noRemoved')}
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSyncModal(false)} color="primary">
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid2 size={{ xs: 12 }}>
                <TimeslotCalendar
                    timeslots={data?.timeslots}
                    disabled={loading}
                    pendingTimeslotId={deletingTimeslotId}
                    onTimeslotSelect={
                        canManageTimeslots
                            ? (timeslotId) =>
                                  navigate(`/shows/${showId}/timeslots/edit/${timeslotId}`)
                            : undefined
                    }
                    onTimeslotDelete={canManageTimeslots ? handleDeleteTimeslot : undefined}
                    onSeatAssign={
                        canAssignSeats && delayedSeatAssignmentEnabled
                            ? (timeslotId) =>
                                  navigate(`/shows/${showId}/timeslots/${timeslotId}/assign-seats`)
                            : undefined
                    }
                    canInlineEdit={canManageTimeslots}
                    ticketTypes={ticketTypes}
                    onInlineEditSave={handleInlineEditSave}
                    onEditRentalDay={
                        canManageTimeslots && rentalMode
                            ? (groupId) =>
                                  navigate(`/shows/${showId}/timeslots/rental-day/${groupId}`)
                            : undefined
                    }
                    slotTemplate={getSlotTemplate(show)}
                />
            </Grid2>
        </PageWithTitle>
    );
};

export default TimeslotsPage;
